import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type {
  CatalogImportJob,
  ListProductsServicesQuery,
  ProductServiceKind,
  ProductServiceResource,
  ProductServiceSortField,
} from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { AssetImage } from '../../components/media/AssetImage';
import { DataGrid, useGridState, type GridColumn } from '../../components/data-grid/DataGrid';
import { useAuth } from '../../auth/AuthContext';
import {
  useCatalogImportJobs,
  useDeleteProductService,
  useProductCategories,
  useProductServiceCounts,
  useProductsServices,
  useSetProductsInWhatsAppCatalog,
  useUnarchiveProductService,
} from '../../api/products-services';
import { useCatalogSyncStatuses, type ProductCatalogSync } from '../../api/whatsapp-catalog';
import { ImportModal } from './ImportModal';
import { AiCatalogImportModal } from './AiCatalogImportModal';

function catalogJobStatusTone(status: CatalogImportJob['status']): string | undefined {
  if (status === 'FAILED') return 'var(--color-danger)';
  if (status === 'DONE') return 'var(--color-success)';
  return undefined;
}

function OngoingImportRow({ job }: { job: CatalogImportJob }) {
  const ongoing = job.status === 'PENDING' || job.status === 'PROCESSING';
  return (
    <li
      style={{
        display: 'grid',
        gap: 'var(--space-1)',
        borderBottom: '1px solid var(--color-border)',
        paddingBottom: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          {new Date(job.createdAt).toLocaleString()}
        </span>
        <span className="ne-badge" style={{ color: catalogJobStatusTone(job.status) }}>
          {job.status}
        </span>
      </div>
      {ongoing ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Reading the file…
        </p>
      ) : job.status === 'FAILED' && job.errorMessage ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)' }}>
          {job.errorMessage}
        </p>
      ) : job.status === 'DONE' ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Preview ready - open "Scan a catalog photo/PDF" again to review and confirm it, or check
          your notifications.
        </p>
      ) : null}
    </li>
  );
}

/** One badge per product for the WhatsApp catalog: synced, or why not (worst state across channels). */
function catalogStateByProduct(rows: ProductCatalogSync[] | undefined) {
  const out = new Map<string, { ok: boolean; error: string | null }>();
  for (const r of rows ?? []) {
    if (r.status === 'REMOVED') continue;
    const prev = out.get(r.productServiceId);
    if (r.status === 'FAILED') out.set(r.productServiceId, { ok: false, error: r.errorMessage });
    else if (!prev) out.set(r.productServiceId, { ok: true, error: null });
  }
  return out;
}

type Tab = 'ALL' | ProductServiceKind;

/** Grid state -> list query (filters are strings in the grid). */
function toListQuery(
  tab: Tab,
  s: { page: number; pageSize: number; sortBy: string; sortOrder: 'asc' | 'desc' },
  f: Record<string, string>,
): ListProductsServicesQuery {
  const num = (v: string | undefined) =>
    v !== undefined && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : undefined;
  return {
    page: s.page,
    pageSize: s.pageSize,
    sortBy: s.sortBy as ProductServiceSortField,
    sortOrder: s.sortOrder,
    kind: tab === 'ALL' ? undefined : tab,
    name: f.name,
    sku: f.sku,
    unit: f.unit,
    category: f.category || undefined,
    status: (f.status as ListProductsServicesQuery['status']) || undefined,
    priceMin: num(f.priceAmountMin),
    priceMax: num(f.priceAmountMax),
    inWhatsAppCatalog: f.inWhatsAppCatalog ? f.inWhatsAppCatalog === 'true' : undefined,
  };
}

function Price({ it }: { it: ProductServiceResource }) {
  if (it.priceAmount === null) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  return it.discountPercent ? (
    <span>
      <span style={{ textDecoration: 'line-through', color: 'var(--color-text-muted)' }}>
        {it.currency} {it.priceAmount}
      </span>{' '}
      {it.currency} {it.discountedPriceAmount}{' '}
      <span style={{ color: 'var(--color-text-muted)' }}>(-{it.discountPercent}%)</span>
    </span>
  ) : (
    <span>
      {it.currency} {it.priceAmount}
    </span>
  );
}

/**
 * Products & services catalog. `kind: SERVICE` with a booking schedule is
 * also the catalog the appointment-booking conversation flow offers to
 * customers (docs/API_SPEC.md section 15) - configure a service's weekly
 * hours here, then use the "Appointment Booking (full flow)" automation
 * template to put it live on WhatsApp. Built on the standard `DataGrid`
 * (server-side sort, column filters, paging) with All / Products / Services
 * tabs.
 */
export function ProductsServicesPage() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = hasPermission('products.manage');

  const [tab, setTab] = useState<Tab>('ALL');
  const [grid, setGrid] = useGridState({ sortBy: 'name' });
  const categories = useProductCategories();
  const query = toListQuery(tab, grid, grid.filters);
  const items = useProductsServices(query);
  const counts = useProductServiceCounts(query);
  const catalogState = catalogStateByProduct(useCatalogSyncStatuses().data);

  const setInCatalog = useSetProductsInWhatsAppCatalog();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const del = useDeleteProductService();
  const unarchive = useUnarchiveProductService();
  const [importOpen, setImportOpen] = useState(false);
  const [aiImportOpen, setAiImportOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Dashboard quick-action deep links (`?quickAction=import|scan`) -
  // auto-open the matching modal once, then strip the param so a
  // refresh/back doesn't reopen it.
  useEffect(() => {
    const quickAction = searchParams.get('quickAction');
    if (quickAction === 'import' || quickAction === 'scan') {
      if (quickAction === 'import') setImportOpen(true);
      if (quickAction === 'scan') setAiImportOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('quickAction');
        return next;
      });
    }
  }, [searchParams, setSearchParams]);

  const catalogJobs = useCatalogImportJobs(canManage);
  const ongoingOrRecentCatalogJobs = (catalogJobs.data ?? []).filter(
    (j) => j.status === 'PENDING' || j.status === 'PROCESSING' || j.status === 'FAILED',
  );

  async function bulkCatalog(include: boolean) {
    try {
      const { updated } = await setInCatalog.mutateAsync({
        productServiceIds: [...selected],
        include,
      });
      toast.success(
        include
          ? `${updated} product(s) added to the WhatsApp catalog.`
          : `${updated} product(s) removed from the WhatsApp catalog.`,
      );
      setSelected(new Set());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the WhatsApp catalog.');
    }
  }

  async function remove(id: string, name: string) {
    if (!window.confirm(`Archive "${name}"? It will no longer be offered to customers.`)) return;
    try {
      await del.mutateAsync(id);
      toast.success('Archived.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not archive this item.');
    }
  }

  async function restore(item: ProductServiceResource) {
    try {
      await unarchive.mutateAsync(item);
      toast.success('Restored to Active.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not restore this item.');
    }
  }

  const columns: GridColumn<ProductServiceResource>[] = [
    {
      key: 'photo',
      header: 'Photo',
      width: 56,
      render: (it) => <AssetImage assetId={it.imageAssetId} alt={it.name} size={40} />,
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      filter: { type: 'text', placeholder: 'Name or local name' },
      render: (it) => (
        <div>
          <strong>{it.name}</strong>
          {it.kind === 'SERVICE' ? (
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
              Service · {it.booking ? 'bookable' : 'no schedule'}
            </div>
          ) : null}
        </div>
      ),
    },
    ...(tab === 'ALL'
      ? [
          {
            key: 'kind',
            header: 'Type',
            sortable: true,
            render: (it: ProductServiceResource) => (
              <span className="ne-badge">{it.kind === 'PRODUCT' ? 'Product' : 'Service'}</span>
            ),
          },
        ]
      : []),
    {
      key: 'sku',
      header: 'SKU',
      sortable: true,
      filter: { type: 'text' },
      render: (it) => it.sku ?? <span style={{ color: 'var(--color-text-muted)' }}>—</span>,
    },
    {
      key: 'category',
      header: 'Category',
      filter: {
        type: 'select',
        options: (categories.data ?? []).map((c) => ({
          value: c.name,
          label: `${c.name} (${c.count})`,
        })),
      },
      render: (it) => it.category ?? <span style={{ color: 'var(--color-text-muted)' }}>—</span>,
    },
    {
      key: 'priceAmount',
      header: 'Price',
      sortable: true,
      filter: { type: 'range' },
      render: (it) => <Price it={it} />,
    },
    {
      key: 'unit',
      header: 'Unit',
      sortable: true,
      filter: { type: 'text' },
      render: (it) => it.unit ?? <span style={{ color: 'var(--color-text-muted)' }}>—</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      filter: {
        type: 'select',
        options: [
          { value: 'ACTIVE', label: 'Active' },
          { value: 'ARCHIVED', label: 'Archived' },
        ],
      },
      render: (it) => <span className="ne-badge">{it.status}</span>,
    },
    {
      key: 'inWhatsAppCatalog',
      header: 'WhatsApp catalog',
      filter: {
        type: 'select',
        options: [
          { value: 'true', label: 'In catalog' },
          { value: 'false', label: 'Not in catalog' },
        ],
      },
      render: (it) => {
        if (!it.inWhatsAppCatalog) {
          return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
        }
        const state = catalogState.get(it.id);
        if (state?.ok) {
          return (
            <span className="ne-badge" style={{ color: 'var(--color-success)' }}>
              WhatsApp catalog ✓
            </span>
          );
        }
        if (state) {
          return (
            <span
              className="ne-badge"
              style={{ color: 'var(--color-danger)' }}
              title={state.error ?? undefined}
            >
              Not in WhatsApp catalog: {state.error ?? 'sync failed'}
            </span>
          );
        }
        return (
          <span className="ne-badge" title="Switched on - syncs to the connected catalog">
            WhatsApp catalog
          </span>
        );
      },
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      sortable: true,
      render: (it) => new Date(it.updatedAt).toLocaleDateString(),
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: '',
            render: (it: ProductServiceResource) => (
              <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/products-services/${it.id}/edit`)}
                >
                  Edit
                </Button>
                {it.status === 'ARCHIVED' ? (
                  <Button
                    variant="secondary"
                    disabled={unarchive.isPending}
                    onClick={() => void restore(it)}
                  >
                    Unarchive
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    disabled={del.isPending}
                    onClick={() => void remove(it.id, it.name)}
                  >
                    Archive
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  const tabs: { id: Tab; label: string; count: number | undefined }[] = [
    { id: 'ALL', label: 'All', count: counts.data?.all },
    { id: 'PRODUCT', label: 'Products', count: counts.data?.product },
    { id: 'SERVICE', label: 'Services', count: counts.data?.service },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1200 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Products & Services</h1>
        <span style={{ flex: 1 }} />
        {canManage ? (
          <>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              Import from CSV/Excel
            </Button>
            <Button variant="secondary" onClick={() => setAiImportOpen(true)}>
              Scan a catalog photo/PDF
            </Button>
            <Button onClick={() => navigate('/products-services/new')}>New product/service</Button>
          </>
        ) : null}
      </div>

      {importOpen ? <ImportModal onClose={() => setImportOpen(false)} /> : null}
      {aiImportOpen ? <AiCatalogImportModal onClose={() => setAiImportOpen(false)} /> : null}

      {canManage && ongoingOrRecentCatalogJobs.length > 0 ? (
        <Card title="Ongoing catalog imports">
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            {ongoingOrRecentCatalogJobs.map((job) => (
              <OngoingImportRow key={job.id} job={job} />
            ))}
          </ul>
        </Card>
      ) : null}

      <Card title="Catalog">
        <div role="tablist" aria-label="Item type" className="ne-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className="ne-tabs__tab"
              onClick={() => {
                setTab(t.id);
                setGrid({ ...grid, page: 1 });
              }}
            >
              {t.label}
              {t.count !== undefined ? <span className="ne-tabs__count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {canManage && selected.size > 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
              margin: 'var(--space-3) 0 0',
            }}
          >
            <span>{selected.size} selected</span>
            <Button disabled={setInCatalog.isPending} onClick={() => void bulkCatalog(true)}>
              Add to WhatsApp catalog
            </Button>
            <Button
              variant="secondary"
              disabled={setInCatalog.isPending}
              onClick={() => void bulkCatalog(false)}
            >
              Remove from WhatsApp catalog
            </Button>
            <Button variant="secondary" onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </div>
        ) : null}

        <div style={{ marginTop: 'var(--space-3)' }}>
          {items.isError ? (
            <PageState
              kind="error"
              message={items.error.message}
              onRetry={() => void items.refetch()}
            />
          ) : (
            <DataGrid
              columns={columns}
              rows={items.data?.items ?? []}
              rowKey={(it) => it.id}
              rowLabel={(it) => it.name}
              total={items.data?.meta.total ?? 0}
              state={grid}
              onStateChange={setGrid}
              loading={items.isFetching}
              emptyMessage={
                Object.keys(grid.filters).length > 0
                  ? 'Nothing matches these filters.'
                  : 'Nothing here yet - add a product or a bookable service above.'
              }
              selection={
                canManage
                  ? {
                      selected,
                      onChange: setSelected,
                      selectable: (it) => it.kind === 'PRODUCT',
                    }
                  : undefined
              }
            />
          )}
        </div>
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/automation-templates">Automation templates</Link> ·{' '}
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
